/**
 * The calendar body shared by the datepicker and the daterange.
 *
 * Names come from `buildDateLocale` (Intl), never from a table in this package.
 *
 * Its anatomy is the contract's: `__grid` holds a weekday header row and one `__row` per week,
 * each row holding seven `__cell`s. That nesting is not decoration — the shipped themes lay out
 * the seven columns on the row, so a flat list of cells renders as a single column.
 */
import { buildMonthGrid, calendarYearRange, isMonthOutOfRange, isYearOutOfRange } from "@modyra/core/datetime";
import { MDY_WIDGET_CONTRACTS, calendarDayId, calendarViewOnZoom, keyBindingFor, partClasses, projectCalendarPeriodCellA11y, projectCalendarViewA11y, } from "@modyra/widgets";
import { applyPart, el, setText } from "../dom.js";
function classesOf(kind, part) {
    const parts = MDY_WIDGET_CONTRACTS[kind].parts;
    return (parts[part]?.classes ?? []).join(" ");
}
/** Builds an empty grid element; `fillCalendar` renders a month into it. */
export function buildCalendarGrid(kind) {
    const grid = el("div", classesOf(kind, "grid"));
    grid.setAttribute("role", "grid");
    return grid;
}
/** Renders one month, replacing whatever the grid held. Returns the day cells by ISO date. */
export function fillCalendar(grid, kind, year, month, locale, onPick, 
/**
 * The widget these days belong to, so each cell carries the id the contract names for it.
 *
 * A day is what `aria-activedescendant` points at and what a document writes down to reach one
 * cell; without an id there is nothing to point at and nothing to name.
 */
widgetId) {
    const firstDayOfWeek = locale.firstDayOfWeek;
    grid.replaceChildren();
    const weekdays = el("div", classesOf(kind, "weekdays"));
    weekdays.setAttribute("role", "row");
    for (let index = 0; index < 7; index += 1) {
        // Sunday-first tables rotated by the locale's own first day — both come from Intl via
        // `buildDateLocale`, so a plain-rendered calendar reads in the user's language.
        const day = (firstDayOfWeek + index) % 7;
        const weekday = el("span", classesOf(kind, "weekday"));
        weekday.setAttribute("role", "columnheader");
        weekday.setAttribute("aria-label", locale.dayNamesShort[day]);
        setText(weekday, locale.dayNamesNarrow[day]);
        weekdays.appendChild(weekday);
    }
    grid.appendChild(weekdays);
    const cells = new Map();
    const month6 = buildMonthGrid(year, month, firstDayOfWeek);
    for (let start = 0; start < month6.length; start += 7) {
        const row = el("div", classesOf(kind, "row"));
        row.setAttribute("role", "row");
        for (const cell of month6.slice(start, start + 7)) {
            const button = el("button", classesOf(kind, "gridcell"));
            button.type = "button";
            button.setAttribute("role", "gridcell");
            button.setAttribute("aria-label", cell.iso);
            if (widgetId !== undefined && widgetId !== "")
                button.id = calendarDayId(widgetId, cell.iso);
            if (!cell.inMonth)
                button.classList.add("mdy-datepicker__cell--outside");
            setText(button, String(cell.date.day));
            button.addEventListener("click", () => onPick(cell));
            row.appendChild(button);
            cells.set(cell.iso, button);
        }
        grid.appendChild(row);
    }
    return cells;
}
/**
 * Builds the month and year views for a calendar of `kind`, drawn from the contract's projection:
 * which cells there are, which is chosen, which the bounds refuse and what each announces are its
 * answers, not the renderer's.
 *
 * Both views exist from the start, hidden: an element that only gains its classes when it is shown
 * is an element no test and no theme can name while it is not.
 */
export function buildPeriodViews(kind, widgetId, locale, 
/** Read at each render rather than once: the bounds a field is given can move after it mounts. */
bounds, onPick) {
    const monthPicker = el("div");
    const yearPicker = el("div");
    applyPart(monthPicker, projectCalendarViewA11y("months", { kind, widgetId }));
    applyPart(yearPicker, projectCalendarViewA11y("years", { kind, widgetId }));
    monthPicker.hidden = true;
    yearPicker.hidden = true;
    function render(mode, year, month) {
        const { min, max } = bounds();
        for (const [host, own] of [[monthPicker, "months"], [yearPicker, "years"]]) {
            host.hidden = mode !== own;
            if (mode !== own)
                continue;
            const values = own === "months"
                ? Array.from({ length: 12 }, (_, index) => index + 1)
                : [...calendarYearRange(year, min, max)];
            const current = own === "months" ? month : year;
            host.replaceChildren(...values.map((value) => {
                const button = el("button");
                button.type = "button";
                setText(button, own === "months" ? (locale.monthNamesShort[value - 1] ?? String(value)) : String(value));
                applyPart(button, projectCalendarPeriodCellA11y(own, {
                    value,
                    label: button.textContent ?? String(value),
                    selected: value === current,
                    disabled: own === "months" ? isMonthOutOfRange(year, value, min, max) : isYearOutOfRange(value, min, max),
                }, { kind, widgetId }));
                button.addEventListener("click", () => {
                    const held = document.activeElement === button;
                    onPick(own === "months" ? { type: "select-month", month: value } : { type: "select-year", year: value });
                    // A year narrows to its months, and the year view is redrawn hidden: focus held on the
                    // pressed year would sit on an element in no accessibility tree. It lands on the chosen
                    // month, or the first the bounds allow, once the months are drawn.
                    if (own === "years" && held) {
                        queueMicrotask(() => (monthPicker.querySelector('[aria-selected="true"]:not([disabled])')
                            ?? monthPicker.querySelector("button:not([disabled])"))?.focus());
                    }
                });
                return button;
            }));
            if (own === "years") {
                // Guarded: a document without layout has no `scrollIntoView`, and a picker that throws
                // there takes the whole effect down with it.
                host
                    .querySelector(`.${partClasses(kind, "yearCell", { selected: true }).join(".")}`)
                    ?.scrollIntoView?.({ block: "center" });
            }
        }
    }
    return { monthPicker, yearPicker, render };
}
/**
 * Wires the gesture the kind declares for stepping between views — the platform's accelerator held
 * with a vertical arrow — on every element a press inside the calendar reaches, and the way back to
 * the days on a dismissal. Bound before the panel's own dismissal, which it stops from a wider view.
 *
 * Handled once per press: a press inside the panel reaches each of `listeners`, and a second reading
 * of the same event would step twice. The keys follow the eye: the view a step came from is hidden,
 * so focus left there would sit on an element in no accessibility tree at all, and the landing is
 * the chosen cell of the view reached, or its first choosable one.
 */
export function bindViewZoom(kind, listeners, hosts, state, onZoom) {
    const zoomed = new WeakSet();
    const onZoomView = (event) => {
        const current = state();
        if (!current.open || zoomed.has(event))
            return;
        const binding = keyBindingFor(kind, event, current.open);
        // A dismissal that keeps the key leaves the view it is in, and only the days give way to a closed
        // panel: stopped here, so the panel's own dismissal never hears it. `Tab` is a dismissal too, but
        // it carries the keyboard onward and is not this view's to take.
        const back = binding?.intent === "cancel" && binding.restoresFocus === true && current.viewMode !== "days";
        const by = binding?.intent === "view" ? binding.by : undefined;
        if (!back && by === undefined)
            return;
        event.preventDefault();
        if (back)
            event.stopImmediatePropagation();
        zoomed.add(event);
        const reached = by === undefined ? "days" : calendarViewOnZoom(current.viewMode, by);
        if (reached === current.viewMode)
            return;
        onZoom(reached);
        queueMicrotask(() => {
            const host = hosts[reached];
            const landing = host.querySelector('[aria-selected="true"], [tabindex="0"]')
                ?? host.querySelector("button:not([disabled])");
            landing?.focus();
        });
    };
    for (const element of listeners)
        element.addEventListener("keydown", onZoomView);
}
